import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { syncChatfoodPages, ChatfoodSyncResult } from '@/services/api/chatfood-sync';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

export default function ChatfoodSync() {
    const [token, setToken] = useState('');
    const [userIdsRaw, setUserIdsRaw] = useState('');
    const [result, setResult] = useState<ChatfoodSyncResult | null>(null);
    const { toast } = useToast();

    const mutation = useMutation({
        mutationFn: () => {
            const user_ids = userIdsRaw
                .split(/[\s,]+/)
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => !isNaN(n));
            return syncChatfoodPages({ token, user_ids });
        },
        onSuccess: (data) => {
            setResult(data);
            toast({ title: 'Sync concluído', description: `${data.pages_updated} páginas | ${data.vars_saved} variáveis` });
        },
        onError: (err: any) => {
            toast({
                title: 'Erro no sync',
                description: err?.response?.data?.detail || err.message,
                variant: 'destructive',
            });
        },
    });

    return (
        <div className="max-w-lg mx-auto mt-16 p-6 space-y-6">
            <h1 className="text-xl font-semibold">Sync ChatFood</h1>

            <div className="space-y-2">
                <Label htmlFor="token">CHATFOOD_TOKEN</Label>
                <Input
                    id="token"
                    type="password"
                    placeholder="eyJ..."
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="user_ids">User IDs (separados por vírgula ou espaço)</Label>
                <Input
                    id="user_ids"
                    placeholder="42, 87, 103"
                    value={userIdsRaw}
                    onChange={(e) => setUserIdsRaw(e.target.value)}
                />
            </div>

            <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || !token || !userIdsRaw}
                className="w-full"
            >
                {mutation.isPending ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sincronizando...
                    </>
                ) : (
                    'Sincronizar'
                )}
            </Button>

            {result && (
                <div className="rounded-md border p-4 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Matched</span><span>{result.matched}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Skipped</span><span>{result.skipped}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Páginas atualizadas</span><span>{result.pages_updated}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Variáveis salvas</span><span>{result.vars_saved}</span></div>
                    {result.errors.length > 0 && (
                        <div className="mt-2 pt-2 border-t space-y-1">
                            <p className="text-muted-foreground font-medium">Erros</p>
                            {result.errors.map((e, i) => (
                                <div key={i} className="text-destructive text-xs">
                                    {e.page_id}: {e.error}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
